import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth/AuthProvider";
import type { TaskPatch } from "../../api/collab";
import { shouldAutoFocusEditable } from "../../lib/mobileFocus";
import { filePreviewKind, splitFilesByRole } from "../../lib/filePreview";
import { GeneratedFilePreview } from "../../components/GeneratedFilePreview";
import { MarkdownMessage } from "../../components/MarkdownMessage";
import type { AgentFile } from "../../types/api";
import type { AgentProfile, Channel, ChannelMessage, ChannelTask, TaskOwnerType, WorkspaceMember } from "../../types/collab";
import { Avatar } from "./avatar";
import { ChannelTasks } from "./ChannelTasks";

interface Props {
  channel: Channel;
  messages: ChannelMessage[];
  tasks: ChannelTask[];
  /** Loaded thread replies keyed by root message id. */
  threads: Record<string, ChannelMessage[]>;
  memberNames: Record<string, string>;
  members: WorkspaceMember[];
  agents: AgentProfile[];
  memberCount: number;
  currentUserId: string;
  sendError: string | null;
  onSend: (body: string) => Promise<void>;
  onSendReply: (rootId: string, body: string) => Promise<void>;
  onOpenThread: (rootId: string) => Promise<void>;
  onCloseThread: (rootId: string) => void;
  onCreateTask: (input: { title: string; description?: string; ownerType?: TaskOwnerType | null; ownerId?: string | null }) => Promise<void>;
  onUpdateTask: (taskId: string, patch: TaskPatch) => Promise<void>;
  onConvertToTask: (messageId: string) => Promise<void>;
}

function timeOf(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

interface MentionOption {
  key: string;
  /** Text inserted after the `@`. */
  insert: string;
  label: string;
  sub?: string;
  kind: "agent" | "human";
}

/** The `@word` fragment the caret is inside, or null. */
function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const match = /(^|[\s])@([\w-]{0,30})$/.exec(upto);
  if (!match) return null;
  return { start: caret - match[2].length - 1, query: match[2] };
}

/**
 * A composer with Slack-style @autocomplete: typing `@` pops a picker over the
 * workspace's agents (first) and human members; ↑↓ to move, Enter/Tab to pick,
 * Esc to close. Enter (no popup) sends.
 */
function MentionComposer({
  placeholder,
  compact,
  agents,
  members,
  onSubmit,
  autoFocus,
  inputId,
}: {
  placeholder: string;
  compact?: boolean;
  agents: AgentProfile[];
  members: WorkspaceMember[];
  onSubmit: (body: string) => Promise<void>;
  autoFocus?: boolean;
  inputId?: string;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const options = useMemo<MentionOption[]>(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    const agentOpts = agents
      .filter((a) => a.handle.toLowerCase().startsWith(q) || a.displayName.toLowerCase().includes(q))
      .map<MentionOption>((a) => ({ key: a.id, insert: a.handle, label: `@${a.handle}`, sub: a.displayName, kind: "agent" }));
    const humanOpts = members
      .map((m) => ({ m, name: m.username ? `@${m.username}` : m.name || m.email || m.userId }))
      .filter(({ name }) => name.toLowerCase().includes(q))
      .map<MentionOption>(({ m, name }) => ({
        key: m.userId,
        insert: m.username || name.replace(/^@/, ""),
        label: name,
        kind: "human",
      }));
    return [...agentOpts, ...humanOpts].slice(0, 8);
  }, [mention, agents, members]);

  useEffect(() => setIndex(0), [mention?.query]);

  const refreshMention = (value: string, caret: number) => {
    setMention(mentionQueryAt(value, caret));
  };

  const pick = (opt: MentionOption) => {
    if (!mention) return;
    const caret = inputRef.current?.selectionStart ?? draft.length;
    const next = `${draft.slice(0, mention.start)}@${opt.insert} ${draft.slice(caret)}`;
    setDraft(next);
    setMention(null);
    // restore focus and put the caret right after the inserted mention
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const pos = mention.start + opt.insert.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const submit = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await onSubmit(body);
      setDraft("");
      setMention(null);
    } catch {
      // The parent renders the request error. Keep the draft so retrying does
      // not require the user to retype the message.
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && options.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIndex((i) => (i + 1) % options.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setIndex((i) => (i - 1 + options.length) % options.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(options[index]);
        return;
      }
      if (e.key === "Escape") {
        setMention(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className={`ws-composer${compact ? " compact" : ""}`}>
      {mention && options.length > 0 ? (
        <div className="ws-mention-pop" role="listbox">
          {options.map((opt, i) => (
            <button
              key={`${opt.kind}:${opt.key}`}
              type="button"
              role="option"
              aria-selected={i === index}
              className={`ws-mention-item${i === index ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(opt);
              }}
              onMouseEnter={() => setIndex(i)}
            >
              <span className={`ws-mention-kind ${opt.kind}`}>{opt.kind === "agent" ? "🤖" : "👤"}</span>
              <span className="ws-mention-label">{opt.label}</span>
              {opt.sub ? <span className="ws-mention-sub">{opt.sub}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      <textarea
        id={inputId}
        ref={inputRef}
        autoFocus={autoFocus}
        className="ws-composer-input"
        rows={1}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          refreshMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
        }}
        onKeyDown={onKeyDown}
        onClick={(e) => refreshMention(draft, (e.target as HTMLTextAreaElement).selectionStart ?? draft.length)}
        onBlur={() => window.setTimeout(() => setMention(null), 120)}
      />
      <button type="button" className="ws-btn primary" onClick={() => void submit()} disabled={!draft.trim() || sending}>
        {t("collab.channels.send")}
      </button>
    </div>
  );
}

/** Raft-style lifecycle chip on a message that @triggered agents. */
function StatusChip({ status }: { status: NonNullable<ChannelMessage["agentStatus"]> }) {
  const { t } = useI18n();
  return (
    <span className={`ws-status ${status}`}>
      {status === "running" ? <span className="ws-status-spin" aria-hidden /> : status === "completed" ? "✓ " : "✗ "}
      {t(`collab.status.${status}`)}
    </span>
  );
}

/** The selected channel's header + tabs + conversation + composer (column 3). */
export function ChannelView({
  channel,
  messages,
  tasks,
  threads,
  memberNames,
  members,
  agents,
  memberCount,
  currentUserId,
  sendError,
  onSend,
  onSendReply,
  onOpenThread,
  onCloseThread,
  onCreateTask,
  onUpdateTask,
  onConvertToTask,
}: Props) {
  const { t } = useI18n();
  const { client, account } = useAuth();
  const [tab, setTab] = useState<"chat" | "tasks" | "files">("chat");
  const [openThreads, setOpenThreads] = useState<Set<string>>(new Set());
  const [converting, setConverting] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ messageId: string; file: AgentFile } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (tab === "chat") endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, channel.id, tab]);

  useEffect(() => {
    setOpenThreads(new Set());
    setPreview(null);
    setFileError(null);
  }, [channel.id]);

  const fetchPreviewFile = useCallback(
    async (file: AgentFile) => {
      if (!client || !account || !preview) throw new Error(t("agent.preview.unavailable"));
      return client.downloadChannelMessageFile(account.id, channel.id, preview.messageId, file.path);
    },
    [account, channel.id, client, preview, t]
  );

  const downloadMessageFile = useCallback(
    async (messageId: string, file: AgentFile, rethrow = false) => {
      if (!client || !account) return;
      try {
        setFileError(null);
        const blob = await client.downloadChannelMessageFile(account.id, channel.id, messageId, file.path);
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = file.name;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        setFileError(error instanceof Error ? error.message : t("common.failed"));
        if (rethrow) throw error;
      }
    },
    [account, channel.id, client, t]
  );

  const authorLabel = useCallback(
    (m: ChannelMessage): string => {
      if (m.authorType === "system") return t("collab.channels.system");
      if (m.authorType === "agent") return memberNames[m.authorId] || t("collab.channels.agent");
      if (m.authorId === currentUserId) return t("collab.channels.you");
      return memberNames[m.authorId] || m.authorId;
    },
    [memberNames, currentUserId, t]
  );

  // "AI is working" — names of agents mentioned on any still-running message.
  const workingNames = useMemo(() => {
    const names = new Set<string>();
    for (const m of messages) {
      if (m.agentStatus !== "running") continue;
      for (const men of m.mentions) {
        if (men.type === "agent") names.add(memberNames[men.id] || t("collab.channels.agent"));
      }
    }
    return [...names];
  }, [messages, memberNames, t]);

  const toggleThread = (m: ChannelMessage) => {
    setOpenThreads((prev) => {
      const next = new Set(prev);
      if (next.has(m.id)) {
        next.delete(m.id);
        onCloseThread(m.id);
      } else {
        next.add(m.id);
        void onOpenThread(m.id);
      }
      return next;
    });
  };

  const replyTo = (m: ChannelMessage) => {
    if (!openThreads.has(m.id)) {
      setOpenThreads((prev) => new Set(prev).add(m.id));
      void onOpenThread(m.id);
    }
    requestAnimationFrame(() => document.getElementById(`thread-composer-${m.id}`)?.focus());
  };

  const convertToTask = async (messageId: string) => {
    setConverting((prev) => new Set(prev).add(messageId));
    try {
      await onConvertToTask(messageId);
    } finally {
      setConverting((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  const taskSourceIds = useMemo(() => new Set(tasks.map((task) => task.sourceMessageId).filter(Boolean)), [tasks]);

  const openTaskThread = (rootId: string) => {
    setTab("chat");
    setOpenThreads((prev) => new Set(prev).add(rootId));
    void onOpenThread(rootId);
  };

  const renderMessageFile = (m: ChannelMessage, file: AgentFile) => (
    <div key={file.path} className={`generated-file-entry is-${filePreviewKind(file)}`}>
      <div className="generated-file-row">
        <button
          type="button"
          className="generated-file"
          title={t("agent.preview.open", { name: file.name })}
          onClick={() => {
            setFileError(null);
            setPreview({ messageId: m.id, file });
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
          <span className="generated-file-copy">
            <span className="generated-file-name">{file.name}</span>
            <span className="generated-file-meta">{t(`agent.preview.kind.${filePreviewKind(file)}`)} · {formatBytes(file.bytes)}</span>
          </span>
        </button>
        <button
          type="button"
          className="generated-file-download"
          aria-label={t("agent.downloadFile", { name: file.name })}
          title={t("agent.downloadFile", { name: file.name })}
          onClick={() => void downloadMessageFile(m.id, file)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
        </button>
      </div>
    </div>
  );

  const renderMessage = (m: ChannelMessage, inThread = false) => (
    <div key={m.id} className={`ws-message${m.authorType === "agent" ? " agent" : ""}${inThread ? " threaded" : ""}`}>
      <Avatar name={authorLabel(m)} id={m.authorId} size={inThread ? 26 : 34} />
      <div className="ws-message-main">
        <div className="ws-message-meta">
          <span className="ws-message-author">{authorLabel(m)}</span>
          {m.authorType === "agent" ? <span className="ws-message-tag">{t("collab.channels.agent")}</span> : null}
          <span className="ws-message-time">{timeOf(m.createdAt)}</span>
          {m.agentStatus ? <StatusChip status={m.agentStatus} /> : null}
          {!inThread ? (
            <button type="button" className="ws-msg-action" onClick={() => replyTo(m)}>
              ↩ {t("collab.thread.reply")}
            </button>
          ) : null}
          {taskSourceIds.has(m.id) ? (
            <span className="ws-message-tasked">{t("collab.tasks.linked")}</span>
          ) : (
            <button type="button" className="ws-msg-action" disabled={converting.has(m.id)} onClick={() => void convertToTask(m.id)}>
              + {t("collab.tasks.convert")}
            </button>
          )}
        </div>
        <div className="ws-message-body">
          {m.authorType === "agent" ? (
            <MarkdownMessage
              text={m.body}
              linkedFiles={m.files ?? []}
              onFileLink={(file) => {
                setFileError(null);
                setPreview({ messageId: m.id, file });
              }}
            />
          ) : m.body}
        </div>
        {(m.files?.length ?? 0) > 0 ? (
          <div className="generated-files" aria-label={t("agent.generatedFiles")}>
            <span className="generated-files-title">{t("agent.filesTitle")}</span>
            <div className="generated-files-list">
              {splitFilesByRole(m.files ?? []).deliverables.map((file) => renderMessageFile(m, file))}
            </div>
            {splitFilesByRole(m.files ?? []).references.length > 0 ? (
              <details className="process-files reference-files">
                <summary title={t("agent.referenceFilesHint")}>
                  {t("agent.referenceFiles", { count: splitFilesByRole(m.files ?? []).references.length })}
                </summary>
                <div className="generated-files-list">
                  {splitFilesByRole(m.files ?? []).references.map((file) => renderMessageFile(m, file))}
                </div>
              </details>
            ) : null}
            {splitFilesByRole(m.files ?? []).process.length > 0 ? (
              <details className="process-files">
                <summary title={t("agent.processFilesHint")}>
                  {t("agent.processFiles", { count: splitFilesByRole(m.files ?? []).process.length })}
                </summary>
                <div className="generated-files-list">
                  {splitFilesByRole(m.files ?? []).process.map((file) => renderMessageFile(m, file))}
                </div>
              </details>
            ) : null}
          </div>
        ) : null}

        {!inThread && (m.replyCount > 0 || openThreads.has(m.id)) ? (
          <div className="ws-thread">
            <button type="button" className="ws-thread-toggle" onClick={() => toggleThread(m)}>
              <span className="ws-thread-icon" aria-hidden>💬</span>
              {openThreads.has(m.id)
                ? t("collab.thread.collapse")
                : t("collab.thread.replies", { count: String(m.replyCount) })}
            </button>
            {openThreads.has(m.id) ? (
              <div className="ws-thread-body">
                {(threads[m.id] ?? []).map((r) => renderMessage(r, true))}
                <MentionComposer
                  compact
                  autoFocus={shouldAutoFocusEditable()}
                  inputId={`thread-composer-${m.id}`}
                  placeholder={t("collab.thread.composerPlaceholder")}
                  agents={agents}
                  members={members}
                  onSubmit={(body) => onSendReply(m.id, body)}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="ws-channel-view">
      <GeneratedFilePreview
        file={preview?.file ?? null}
        fetchFile={fetchPreviewFile}
        downloadFile={async (file) => {
          if (preview) await downloadMessageFile(preview.messageId, file, true);
        }}
        onClose={() => setPreview(null)}
        labels={{
          close: t("common.close"),
          download: t("agent.preview.download"),
          htmlSafety: t("agent.preview.htmlSafety"),
          htmlLive: t("agent.preview.htmlLive"),
          imageTooLarge: (name: string) => t("agent.preview.imageTooLarge", { name }),
          imageUnavailable: (name: string) => t("agent.preview.imageUnavailable", { name }),
          kind: {
            markdown: t("agent.preview.kind.markdown"),
            html: t("agent.preview.kind.html"),
            image: t("agent.preview.kind.image"),
            video: t("agent.preview.kind.video"),
            pdf: t("agent.preview.kind.pdf"),
            unsupported: t("agent.preview.kind.unsupported")
          },
          loading: t("agent.preview.loading"),
          outline: t("agent.preview.outline"),
          pdfPage: (page: number, total: number) => t("agent.preview.pdfPage", { page, total }),
          pdfPrev: t("agent.preview.pdfPrev"),
          pdfNext: t("agent.preview.pdfNext"),
          remoteImageBlocked: t("agent.preview.remoteImageBlocked"),
          retry: t("common.retry"),
          tooLarge: t("agent.preview.tooLarge"),
          unavailable: t("agent.preview.unavailable"),
          unsupported: t("agent.preview.unsupported")
        }}
      />
      {fileError ? <p className="ws-warn ws-send-error" role="alert">{fileError}</p> : null}
      <header className="ws-chan-header">
        <div className="ws-chan-headmain">
          <span className="ws-chan-icon">{channel.visibility === "private" ? "🔒" : "#"}</span>
          <div className="ws-chan-headtext">
            <h4 className="ws-chan-title">{channel.name}</h4>
            {channel.topic ? <p className="ws-chan-topic">{channel.topic}</p> : null}
          </div>
        </div>
        <div className="ws-chan-headright">
          <span className="ws-chan-members" title={t("collab.members")}>
            <span className="ws-chan-members-icon">👥</span> {memberCount}
          </span>
        </div>
      </header>

      <nav className="ws-chan-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "chat"} className={`ws-chan-tab${tab === "chat" ? " active" : ""}`} onClick={() => setTab("chat")}>
          {t("collab.tab.chat")}
        </button>
        <button type="button" role="tab" aria-selected={tab === "tasks"} className={`ws-chan-tab${tab === "tasks" ? " active" : ""}`} onClick={() => setTab("tasks")}>
          {t("collab.tab.tasks")}
        </button>
        <button type="button" className="ws-chan-tab disabled" title={t("collab.tab.soon")} disabled>
          {t("collab.tab.files")}
        </button>
      </nav>

      {sendError ? <p className="ws-warn ws-send-error" role="alert">{sendError}</p> : null}

      {tab === "tasks" ? (
        <ChannelTasks
          tasks={tasks}
          members={members}
          agents={agents}
          memberNames={memberNames}
          currentUserId={currentUserId}
          onCreate={onCreateTask}
          onUpdate={onUpdateTask}
          onOpenThread={openTaskThread}
        />
      ) : (
        <>
          <div className="ws-messages">
            {messages.length === 0 ? (
              <p className="ws-content-empty">{t("collab.channels.noMessages")}</p>
            ) : (
              messages.map((m) => renderMessage(m))
            )}
            <div ref={endRef} />
          </div>

          {workingNames.length > 0 ? (
            <div className="ws-working" role="status">
              <span className="ws-working-dots" aria-hidden>
                <i /><i /><i />
              </span>
              {t("collab.working", { names: workingNames.join("、") })}
            </div>
          ) : null}

          <MentionComposer
            placeholder={t("collab.channels.composerPlaceholder", { name: channel.name })}
            agents={agents}
            members={members}
            onSubmit={onSend}
          />
        </>
      )}
    </div>
  );
}
